import api from './base-api';
import {
	PrototypeH2HAnalytics,
	PrototypeH2HLeaderboardEntry,
	PrototypeH2HMatch,
	PrototypeH2HTag,
	PrototypeH2HUserStats,
	SelectMatchRequest,
	MarketDto,
	UserMatchDto,
	SlateByEventDto,
	LobbyByEventDto,
} from '@sportspredict/shared';

interface SlatesByEventQueryArgs {
	eventId: string;
	lobbyId?: string;
}

export const headToHeadApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// PUBLIC
		getLobby: builder.query<any, string>({
			query: (lobbyId) => ({
				url: `lobbies/${lobbyId}`,
			}),
			providesTags: (result, error, lobbyId) => [{ type: 'Lobby', id: lobbyId }],
			extraOptions: { skipAuth: true },
		}),
		getEvent: builder.query<any, string>({
			query: (eventId) => ({
				url: `events/${eventId}`,
			}),
			providesTags: (result, error, eventId) => [{ type: 'Event', id: eventId }],
			extraOptions: { skipAuth: true },
		}),
		getLobbiesByEvent: builder.query<LobbyByEventDto[], string>({
			query: (eventId) => ({
				url: `lobbies/event/${eventId}`,
			}),
			providesTags: (result, error, eventId) => [{ type: 'Lobby', id: eventId }],
			extraOptions: { skipAuth: true },
		}),
		getMarketsByMatch: builder.query<MarketDto[], { matchId: string; lobbyId: string }>({
			query: ({ matchId, lobbyId }) => ({
				url: `markets/${matchId}/markets?lobby_id=${lobbyId}`,
			}),
			providesTags: (result, error, { matchId }) => [{ type: 'Market', id: matchId }],
			extraOptions: { skipAuth: true },
		}),

		// PRIVATE
		selectMatch: builder.mutation<UserMatchDto, SelectMatchRequest>({
			query: (body) => ({
				url: 'users/select-match',
				method: 'POST',
				body,
			}),
			invalidatesTags: (result, error, { matchId }) =>
				matchId
					? ['Game', { type: 'Market' as const, id: matchId }]
					: ['Game', { type: 'Market' as const }],
		}),
		getUserMatches: builder.query<PrototypeH2HMatch[], string>({
			query: (lobbyId) => ({
				url: `h2h-match/lobby/${lobbyId}`,
			}),
			providesTags: (result, error, lobbyId) => [{ type: 'Game', id: lobbyId }],
		}),
		getH2HMatchDetail: builder.query<any, { matchId: string; lobbyId: string }>({
			query: ({ matchId, lobbyId }) => ({
				url: `h2h-match/${matchId}/${lobbyId}/detail`,
			}),
			providesTags: (result, error, { matchId }) => [{ type: 'Game', id: matchId }],
		}),
		getUserStats: builder.query<PrototypeH2HUserStats, string>({
			query: (lobbyId) => ({
				url: `h2h-match/user-stats/${lobbyId}`,
			}),
			providesTags: (result, error, lobbyId) => [{ type: 'Game', id: lobbyId }],
		}),
		getH2HLeaderboard: builder.query<PrototypeH2HLeaderboardEntry[], string>({
			query: (lobbyId) => ({
				url: `h2h-match/leaderboard/${lobbyId}`,
			}),
			providesTags: (result, error, eventId) => [{ type: 'Game', id: eventId }],
		}),
		getH2HVisibleSlates: builder.query<any[], SlatesByEventQueryArgs>({
			query: ({ eventId, lobbyId }) => ({
				url: 'slates/event-head-to-head',
				params: { eventId, ...(lobbyId && { lobbyId }) },
			}),
			providesTags: (result, error, { eventId, lobbyId }) => [
				{ type: 'Game', id: `${eventId}-slates-${lobbyId ?? 'all'}` },
			],
		}),
		getSlatesByEvent: builder.query<SlateByEventDto[], SlatesByEventQueryArgs>({
			query: ({ eventId, lobbyId }) => ({
				url: 'slates/event',
				params: { eventId, ...(lobbyId && { lobbyId }) },
			}),
			providesTags: (result, error, { eventId, lobbyId }) => [
				{ type: 'Game', id: `${eventId}-slates-${lobbyId ?? 'all'}` },
			],
			extraOptions: { skipAuth: true },
		}),
		getH2HAnalytics: builder.query<PrototypeH2HAnalytics, string>({
			query: (h2hMatchId) => ({
				url: `h2h-match/${h2hMatchId}/analytics`,
			}),
			providesTags: (result, error, h2hMatchId) => [{ type: 'Game', id: h2hMatchId }],
		}),
		getH2HTags: builder.query<PrototypeH2HTag[], string>({
			query: (h2hMatchId) => ({
				url: `h2h-match/${h2hMatchId}/tags`,
			}),
			providesTags: (result, error, h2hMatchId) => [{ type: 'Game', id: h2hMatchId }],
		}),
		joinH2HMatch: builder.mutation<{ userMatchId: string }, { matchId: string; lobbyId: string }>({
			query: (body) => ({
				url: 'h2h-match/join',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Game'],
		}),
	}),
});

export const {
	useSelectMatchMutation,
	useGetMarketsByMatchQuery,
	useGetLobbyQuery,
	useGetEventQuery,
	useGetLobbiesByEventQuery,
	useGetUserMatchesQuery,
	useGetUserStatsQuery,
	useGetH2HLeaderboardQuery,
	useGetH2HVisibleSlatesQuery,
	useGetSlatesByEventQuery,
	useGetH2HAnalyticsQuery,
	useGetH2HTagsQuery,
	useJoinH2HMatchMutation,
	useGetH2HMatchDetailQuery,
} = headToHeadApi;

export default headToHeadApi;
